import { useMemo } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { ProductionPhase } from '../types';
import { getLocalDateKey } from './date-keys';


/**
 * Determines the current production phase based on scheduler events
 * 
 * Logic:
 * - Pre-Production: Before first rehearsal event
 * - Rehearsal: First rehearsal → first tech event
 * - Tech: First tech event → first performance
 * - Performance: During performance events
 * - Archival: After last performance
 */
export function useProductionPhase(): ProductionPhase {
    const events = useAppStore(state => state.rehearsalSchedule || []);

    return useMemo(() => {
        if (!events || events.length === 0) {
            return 'pre-production';
        }

        const now = new Date();
        const today = getLocalDateKey(now);

        // Find milestone events
        const rehearsalEvents = events.filter(e => 
            ['scene', 'act', 'run', 'rehearsal-scene'].includes(e.type)
        ).toSorted((a, b) => a.date.localeCompare(b.date));

        const techEvents = events.filter(e => e.type === 'tech')
            .toSorted((a, b) => a.date.localeCompare(b.date));

        const performanceEvents = events.filter(e => e.type === 'performance')
            .toSorted((a, b) => a.date.localeCompare(b.date));

        const firstRehearsal = rehearsalEvents[0]?.date;
        const firstTech = techEvents[0]?.date;
        const firstPerformance = performanceEvents[0]?.date;
        const lastPerformance = performanceEvents[performanceEvents.length - 1]?.date;

        // Determine phase based on current date
        if (lastPerformance && today > lastPerformance) {
            return 'archival';
        }

        if (firstPerformance && today >= firstPerformance) {
            return 'performance';
        }

        if (firstTech && today >= firstTech) {
            return 'tech';
        }

        if (firstRehearsal && today >= firstRehearsal) {
            return 'rehearsal';
        }

        return 'pre-production';
    }, [events]);
}
